import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapTrifold, UploadSimple, X, Trash, MagicWand, GridFour } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { DraggableMarker } from './DraggableMarker';
import { BarnMapEditor } from './BarnMapEditor';

/**
 * Interactive marker editor for an extra (secondary) stall/RV location.
 *
 * Features (parity with primary stall/RV editor):
 *  - Upload / replace / remove the detail map image (with cache-bust)
 *  - Rotate 90° / 180° / 270°
 *  - Zoom + pan + "Draw Region" click-and-drag to create new rectangles
 *  - Each marker is draggable/resizable via DraggableMarker
 *  - Inline barn-name editing + remove
 *  - AI vision Auto-Detect (whole-section flow)
 *  - OpenCV Auto-Detect Cells (per-rectangle flow for grid maps)
 *  - Save all markers via PUT /extra-locations/{loc_id}
 */
export const ExtraLocationMapEditor = ({ showId, location, onChanged }) => {
  const [mapUrl, setMapUrl] = useState(location.detail_map_url || null);
  const [markers, setMarkers] = useState(location.markers || []);
  const [uploading, setUploading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const innerRef = useRef(null);

  // Re-sync when the parent hands us a refreshed location (e.g. after save)
  useEffect(() => {
    setMapUrl(location.detail_map_url || null);
    setMarkers(location.markers || []);
  }, [location.id, location.detail_map_url]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/shows/${showId}/extra-locations/${location.id}/detail-map`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setMapUrl(data.detail_map_url);
      toast.success('Map uploaded');
      onChanged && onChanged();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRotate = async (degrees) => {
    if (rotating) return;
    setRotating(true);
    try {
      const { data } = await api.post(
        `/shows/${showId}/extra-locations/${location.id}/rotate-detail-map`,
        { degrees },
      );
      if (data.refreshed_url) setMapUrl(data.refreshed_url);
      toast.success(`Rotated ${degrees}°`);
      onChanged && onChanged();
    } catch {
      toast.error('Rotation failed');
    } finally {
      setRotating(false);
    }
  };

  // Auto-detect: AI-vision flow returns coarse "sections" (whole barns or
  // groups of rows). Best for hand-drawn maps with labeled regions.
  const [detecting, setDetecting] = useState(false);
  const handleAutoDetect = async () => {
    if (!mapUrl) {
      toast.error('Upload a map first');
      return;
    }
    if (detecting) return;
    setDetecting(true);
    const t = toast.loading('Analyzing map with AI...');
    try {
      const mapType = `loc:${location.id}`;
      const { data } = await api.post(`/shows/${showId}/detect-spots/${mapType}`);
      if (!data.sections || data.sections.length === 0) {
        toast.error('No sections detected. Try uploading a clearer map or use Auto-Detect Cells.', { id: t });
        return;
      }
      // Apply directly — extra location chart + markers are stored on the
      // location object via apply-detected-spots.
      await api.post(`/shows/${showId}/apply-detected-spots`, {
        sections: data.sections,
        map_type: mapType,
        map_target: mapType,
        layout: 'ai',
      });
      toast.success(`Detected ${data.sections.length} sections — saved`, { id: t });
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-detect failed', { id: t });
    } finally {
      setDetecting(false);
    }
  };

  // Per-cell OpenCV detector — cleaner output for grid-style barn maps with
  // many uniform rectangles. Each detected cell becomes its own spot.
  const handleAutoDetectCells = async () => {
    if (!mapUrl) {
      toast.error('Upload a map first');
      return;
    }
    if (detecting) return;
    setDetecting(true);
    const t = toast.loading('Detecting cells...');
    try {
      const mapType = `loc:${location.id}`;
      const { data } = await api.post(`/shows/${showId}/detect-cells/${mapType}`);
      if (!data.barns || data.barns.length === 0) {
        toast.error('No cells detected. The map may need to be a clearer grid layout.', { id: t });
        return;
      }
      await api.post(`/shows/${showId}/apply-cells`, {
        barns: data.barns,
        map_type: mapType,
        start_number: 1,
      });
      toast.success(`Detected ${data.total_cells} cells across ${data.total_barns} barn(s) — saved`, { id: t });
      if (data.refreshed_map_url) setMapUrl(data.refreshed_map_url);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cell detection failed', { id: t });
    } finally {
      setDetecting(false);
    }
  };

  // OCR-based number detector: AI reads every printed number on the map and
  // creates one tappable spot per number even when no rectangle surrounds it.
  // Best for RV / parking maps that just list numbered spots along a lane.
  const handleAutoDetectNumbers = async () => {
    if (!mapUrl) {
      toast.error('Upload a map first');
      return;
    }
    if (detecting) return;
    setDetecting(true);
    const t = toast.loading('Reading numbers off map...');
    try {
      const mapType = `loc:${location.id}`;
      const { data } = await api.post(`/shows/${showId}/detect-numbers/${mapType}`);
      if (!data.barns || data.barns.length === 0) {
        toast.error('No numbers detected. Try a clearer image.', { id: t });
        return;
      }
      await api.post(`/shows/${showId}/apply-cells`, {
        barns: data.barns,
        map_type: mapType,
        start_number: 1,
      });
      toast.success(`Detected ${data.total_cells} numbered spots — saved`, { id: t });
      if (data.refreshed_map_url) setMapUrl(data.refreshed_map_url);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Number detection failed', { id: t });
    } finally {
      setDetecting(false);
    }
  };

  // Auto-Detect Barns: GPT-4o finds every labeled barn on the map and creates
  // a named region per barn. One-click multi-barn setup for additional charts.
  const [autoLabeling, setAutoLabeling] = useState(false);
  const handleAutoDetectBarns = async () => {
    if (!mapUrl) { toast.error('Upload a map first'); return; }
    setAutoLabeling(true);
    const t = toast.loading('Finding labeled barns…');
    try {
      const mapType = `loc:${location.id}`;
      const { data } = await api.post(
        `/shows/${showId}/detect-barn-labels/${mapType}`,
        null,
        { timeout: 180000 },
      );
      const found = data.barns || [];
      if (found.length === 0) {
        toast.error('No barn labels detected. Draw regions manually.', { id: t });
        return;
      }
      const newMarkers = found.map(b => ({
        barn: b.label || 'Barn',
        x: b.bbox.x, y: b.bbox.y, w: b.bbox.w, h: b.bbox.h,
      }));
      const merged = [...markers, ...newMarkers];
      setMarkers(merged);
      try {
        await api.put(`/shows/${showId}/extra-locations/${location.id}`, { markers: merged });
      } catch { /* still visible in UI */ }
      toast.success(`Detected ${found.length} barns: ${found.map(b => b.label).join(', ')}`, { id: t });
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || 'Barn label detection failed', { id: t });
    } finally {
      setAutoLabeling(false);
    }
  };

  // Per-region detection: produce one new barn from a single drawn region.
  const [detectingRegionIdx, setDetectingRegionIdx] = useState(null);
  const [regionPreview, setRegionPreview] = useState(null);
  const [applyingRegion, setApplyingRegion] = useState(false);
  const handleDetectRegion = async (markerIdx, mode) => {
    const m = markers[markerIdx];
    if (!m) return;
    setDetectingRegionIdx(`${markerIdx}:${mode}`);
    setRegionPreview(null);
    try {
      const endpoint = mode === 'cells' ? 'detect-cells-in-region' : 'detect-numbers-in-region';
      const mapType = `loc:${location.id}`;
      const { data } = await api.post(
        `/shows/${showId}/${endpoint}/${mapType}`,
        { region: { x: m.x, y: m.y, w: m.w, h: m.h }, name: m.barn || `Barn ${markerIdx + 1}` },
        { timeout: 180000 },
      );
      setRegionPreview({
        markerIdx,
        barn_name: m.barn || `Barn ${markerIdx + 1}`,
        cells: data.cells || [],
        ocr_hits: data.ocr_hits || 0,
        ocr_total: data.ocr_total || (data.cells || []).length,
        mode,
      });
      toast.success(`Found ${data.total_cells} cells · review preview, then click Apply`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Region detection failed');
    } finally {
      setDetectingRegionIdx(null);
    }
  };
  const handleApplyRegionPreview = async () => {
    if (!regionPreview) return;
    setApplyingRegion(true);
    try {
      await api.post(
        `/shows/${showId}/apply-region-cells/loc:${location.id}`,
        {
          barn_name: regionPreview.barn_name,
          cells: regionPreview.cells,
          start_number: 1,
        },
      );
      toast.success(`Created barn "${regionPreview.barn_name}" with ${regionPreview.cells.length} spots · Refreshing…`);
      setRegionPreview(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingRegion(false);
    }
  };

  // Grid Fill: rows × cols inside a region with no AI detection.
  const [gridFillIdx, setGridFillIdx] = useState(null);
  const [gridRows, setGridRows] = useState(2);
  const [gridCols, setGridCols] = useState(14);
  const [gridStart, setGridStart] = useState(1);
  const [gridReverseTop, setGridReverseTop] = useState(false);
  const [gridReverseEvery, setGridReverseEvery] = useState(false);
  const [gridApplying, setGridApplying] = useState(false);

  const handleOpenGridFill = (markerIdx) => {
    setGridFillIdx(markerIdx);
    setGridRows(2); setGridCols(14); setGridStart(1);
    setGridReverseTop(false); setGridReverseEvery(false);
  };

  const buildGridCells = () => {
    const m = markers[gridFillIdx];
    if (!m || gridRows < 1 || gridCols < 1) return [];
    const cells = [];
    const tileW = m.w / gridCols;
    const tileH = m.h / gridRows;
    const SHRINK = 0.80;
    const cellW = tileW * SHRINK;
    const cellH = tileH * SHRINK;
    const padX = (tileW - cellW) / 2;
    const padY = (tileH - cellH) / 2;
    let num = parseInt(gridStart, 10) || 1;
    for (let r = 0; r < gridRows; r++) {
      const reverseThisRow = gridReverseEvery
        ? (r % 2 === 0 ? gridReverseTop : !gridReverseTop)
        : (r === 0 && gridReverseTop);
      for (let c = 0; c < gridCols; c++) {
        const col = reverseThisRow ? (gridCols - 1 - c) : c;
        cells.push({
          x: +(m.x + col * tileW + padX).toFixed(3),
          y: +(m.y + r * tileH + padY).toFixed(3),
          w: +cellW.toFixed(3),
          h: +cellH.toFixed(3),
          printed_number: num,
        });
        num++;
      }
    }
    return cells;
  };

  const handleApplyGridFill = async () => {
    const m = markers[gridFillIdx];
    if (!m) return;
    const cells = buildGridCells();
    if (cells.length === 0) { toast.error('Invalid grid'); return; }
    setGridApplying(true);
    try {
      await api.post(
        `/shows/${showId}/apply-region-cells/loc:${location.id}`,
        {
          barn_name: m.barn || `Barn ${gridFillIdx + 1}`,
          cells,
          start_number: parseInt(gridStart, 10) || 1,
        },
      );
      toast.success(`Created barn "${m.barn || `Barn ${gridFillIdx + 1}`}" with ${cells.length} spots · Refreshing…`);
      setGridFillIdx(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply grid');
    } finally {
      setGridApplying(false);
    }
  };

  const getPercent = (clientX, clientY) => {
    if (!innerRef.current) return { x: 0, y: 0 };
    const rect = innerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  const handleMouseDown = (e) => {
    if (drawing) {
      e.preventDefault();
      const p = getPercent(e.clientX, e.clientY);
      setDrawStart(p);
      setDrawCurrent(p);
    } else {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (drawing && drawStart) {
      e.preventDefault();
      setDrawCurrent(getPercent(e.clientX, e.clientY));
    } else if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  };

  const handleMouseUp = () => {
    if (drawing && drawStart && drawCurrent) {
      const x = Math.min(drawStart.x, drawCurrent.x);
      const y = Math.min(drawStart.y, drawCurrent.y);
      const w = Math.abs(drawCurrent.x - drawStart.x);
      const h = Math.abs(drawCurrent.y - drawStart.y);
      if (w > 1 && h > 1) {
        setMarkers((prev) => [
          ...prev,
          { barn: '', x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) },
        ]);
      }
      setDrawStart(null);
      setDrawCurrent(null);
      setDrawing(false);
    }
    setIsPanning(false);
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom((z) => Math.min(Math.max(z + (e.deltaY > 0 ? -0.15 : 0.15), 0.5), 5));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel, mapUrl]);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.3, 5));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.3, 0.5));
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const updateMarker = (idx, patch) => {
    setMarkers((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const removeMarker = (idx) => {
    setMarkers((prev) => prev.filter((_, i) => i !== idx));
    if (selected === idx) setSelected(null);
  };

  const saveMarkers = async () => {
    // Allow empty arrays through — producers must be able to clear all regions.
    const valid = markers.filter((m) => (m.barn || '').trim());
    setSaving(true);
    try {
      await api.put(`/shows/${showId}/extra-locations/${location.id}`, { markers: valid });
      setMarkers(valid);
      toast.success(valid.length === 0 ? 'All regions cleared' : 'Regions saved');
      onChanged && onChanged();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Fit All Barns — match the primary chart's bulk-fix flow. We hit
  // `fit-markers-to-printed-cells` for each barn in the extra location's
  // chart that has placed detail_markers. Mirrors v75 on the main chart.
  const [fittingAll, setFittingAll] = useState(false);
  const fitAllBarns = async () => {
    const chart = location.chart || [];
    const targets = chart
      .map((b, idx) => ({ b, idx }))
      .filter(({ b }) => (b.detail_markers || []).length > 0);
    if (targets.length === 0) {
      toast.error('No barns with placed markers yet — run Auto-Detect or Grid Fill first.');
      return;
    }
    if (!window.confirm(
      `Run "Fit to Squares" on ${targets.length} barn${targets.length === 1 ? '' : 's'} in "${location.name}"? `
        + 'This rewrites each barn\'s marker sizes to match the printed cells on the map.',
    )) return;
    setFittingAll(true);
    let fitted = 0; let failed = 0; let total = 0;
    for (const { idx } of targets) {
      try {
        const { data } = await api.post(
          `/shows/${showId}/extra-locations/${location.id}/barns/${idx}/fit-markers-to-printed-cells`,
          null,
          { timeout: 90000 },
        );
        fitted += data.fitted || 0;
        total += (data.fitted || 0) + (data.unchanged || 0);
      } catch {
        failed += 1;
      }
    }
    setFittingAll(false);
    if (failed === 0) {
      toast.success(`Fit ${fitted}/${total} markers across ${targets.length} barns`);
    } else {
      toast.error(`Fit ${fitted}/${total} markers · ${failed} barn${failed === 1 ? '' : 's'} failed`);
    }
    onChanged && onChanged();
  };

  const previewRect = drawStart && drawCurrent ? {
    x: Math.min(drawStart.x, drawCurrent.x),
    y: Math.min(drawStart.y, drawCurrent.y),
    w: Math.abs(drawCurrent.x - drawStart.x),
    h: Math.abs(drawCurrent.y - drawStart.y),
  } : null;

  const variant = location.type === 'rv' ? 'rv' : 'stall';

  if (!mapUrl) {
    return (
      <label
        className="flex flex-col items-center justify-center p-5 border-2 border-dashed border-[#262626] rounded-lg cursor-pointer hover:border-[#0096FF]/50 transition-colors"
        data-testid={`extra-loc-map-dropzone-${location.id}`}
      >
        <UploadSimple size={22} className="text-[#555] mb-1" />
        <span className="text-[11px] text-[#9E9E9E]">
          {uploading ? 'Uploading...' : 'Upload detail map to draw regions'}
        </span>
        <span className="text-[10px] text-[#555] mt-0.5">JPG, PNG or WebP</span>
        <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
      </label>
    );
  }

  return (
    <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3" data-testid={`extra-loc-map-editor-${location.id}`}>
      {/* Controls */}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <button type="button" onClick={zoomOut} className="w-7 h-7 rounded bg-[#161616] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-sm font-bold">−</button>
        <span className="text-[10px] text-[#555] w-10 text-center">{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={zoomIn} className="w-7 h-7 rounded bg-[#161616] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-sm font-bold">+</button>
        <button type="button" onClick={resetView} className="px-2 py-1 rounded bg-[#161616] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white">Reset</button>
        <div className="w-px h-4 bg-[#262626] mx-1" />
        <button type="button" onClick={() => handleRotate(270)} disabled={rotating} className="px-2 py-1 rounded bg-[#161616] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50" title="Rotate counter-clockwise 90°">↺ 90°</button>
        <button type="button" onClick={() => handleRotate(90)} disabled={rotating} className="px-2 py-1 rounded bg-[#161616] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50" title="Rotate clockwise 90°">↻ 90°</button>
        <button type="button" onClick={() => handleRotate(180)} disabled={rotating} className="px-2 py-1 rounded bg-[#161616] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50" title="Flip 180°">180°</button>
        <span className="text-[10px] text-[#555] ml-1 flex-1 min-w-[140px]">
          {drawing ? 'Draw mode: click + drag on map' : 'Scroll to zoom · drag to pan'}
        </span>
      </div>

      {/* Map canvas */}
      <div
        ref={containerRef}
        className={`relative w-full select-none overflow-hidden rounded-md border bg-[#0A0A0A] ${drawing ? 'border-[#FF9500] cursor-crosshair' : 'border-[#262626] cursor-grab'}`}
        style={{ maxHeight: '420px', touchAction: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        data-testid={`extra-loc-canvas-${location.id}`}
      >
        <div
          ref={innerRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: isPanning || drawStart ? 'none' : 'transform 0.15s ease-out',
            position: 'relative',
          }}
        >
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${mapUrl}`}
            alt={`${location.name} detail map`}
            className={`w-full rounded-md border bg-[#0A0A0A] ${drawing ? 'border-[#FF9500]' : 'border-[#262626]'}`}
            style={drawing ? { pointerEvents: 'none' } : {}}
            draggable={false}
          />
          {markers.map((m, i) => (
            <DraggableMarker
              key={i}
              marker={m}
              containerRef={innerRef}
              disabled={drawing}
              selected={selected === i}
              variant={variant}
              label={m.barn || `Region ${i + 1}`}
              onClick={() => setSelected(selected === i ? null : i)}
              onChange={(u) => {
                updateMarker(i, u);
                setSelected(i);
              }}
              testId={`extra-loc-marker-${location.id}-${i}`}
            />
          ))}
          {previewRect && (
            <div
              className="absolute border-2 border-dashed border-[#FF9500] bg-[#FF9500]/10 rounded-sm pointer-events-none"
              style={{ left: `${previewRect.x}%`, top: `${previewRect.y}%`, width: `${previewRect.w}%`, height: `${previewRect.h}%` }}
            />
          )}
          {drawing && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#FF9500] text-black text-[11px] font-bold px-3 py-1 rounded-full pointer-events-none z-10">
              Click and drag to draw a region
            </div>
          )}
        </div>
      </div>

      {/* Marker list */}
      {markers.length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`extra-loc-markers-${location.id}`}>
          <p className="text-[10px] text-[#555] uppercase tracking-wider">Regions ({markers.length})</p>
          {markers.map((m, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 p-1 rounded flex-wrap ${selected === i ? 'bg-[#0096FF]/10 border border-[#0096FF]/30' : ''}`}
              data-testid={`extra-loc-region-row-${location.id}-${i}`}
            >
              <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${variant === 'rv' ? 'bg-[#AF52DE]' : 'bg-[#FF9500]'}`} />
              <input
                value={m.barn || ''}
                onChange={(e) => updateMarker(i, { barn: e.target.value })}
                placeholder="Section / Barn name"
                className="flex-1 min-w-[100px] px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] focus:outline-none focus:border-[#0096FF]"
                data-testid={`extra-loc-marker-name-${location.id}-${i}`}
              />
              <button type="button" onClick={() => handleDetectRegion(i, 'cells')}
                disabled={!!detectingRegionIdx || !!regionPreview}
                title="Detect stall cells inside this region (OpenCV + OCR)."
                className="px-2 py-1 bg-[#FF9500]/15 border border-[#FF9500]/40 rounded text-[10px] text-[#FF9500] hover:bg-[#FF9500]/25 disabled:opacity-50 font-semibold"
                data-testid={`extra-loc-region-detect-cells-${location.id}-${i}`}>
                {detectingRegionIdx === `${i}:cells` ? 'Detecting…' : '⊞ Detect'}
              </button>
              <button type="button" onClick={() => handleDetectRegion(i, 'numbers')}
                disabled={!!detectingRegionIdx || !!regionPreview}
                title="Detect printed numbers inside this region (AI)."
                className="px-2 py-1 bg-[#AF52DE]/15 border border-[#AF52DE]/40 rounded text-[10px] text-[#AF52DE] hover:bg-[#AF52DE]/25 disabled:opacity-50 font-semibold"
                data-testid={`extra-loc-region-detect-numbers-${location.id}-${i}`}>
                {detectingRegionIdx === `${i}:numbers` ? 'Reading…' : '🔢 Read'}
              </button>
              <button type="button" onClick={() => handleOpenGridFill(i)}
                disabled={!!detectingRegionIdx || !!regionPreview}
                title="Create a rows × cols grid inside this region (no AI needed)."
                className="px-2 py-1 bg-[#34C759]/15 border border-[#34C759]/40 rounded text-[10px] text-[#34C759] hover:bg-[#34C759]/25 disabled:opacity-50 font-semibold"
                data-testid={`extra-loc-region-grid-fill-${location.id}-${i}`}>
                ▦ Grid
              </button>
              <button
                type="button"
                onClick={() => removeMarker(i)}
                className="p-0.5 text-[#FF3B30]/60 hover:text-[#FF3B30]"
                title="Remove region"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {/* Region detection live preview */}
          {regionPreview && (
            <div className="mt-2 p-2.5 bg-[#0D1A0D] border border-[#34C759]/40 rounded">
              <p className="text-[11px] font-bold text-[#34C759] mb-1">
                Preview: {regionPreview.cells.length} spots in "{regionPreview.barn_name}" · OCR {regionPreview.ocr_hits}/{regionPreview.ocr_total}
              </p>
              <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={handleApplyRegionPreview} disabled={applyingRegion || regionPreview.cells.length === 0}
                  className="px-2 py-1 bg-[#34C759] rounded text-[10px] text-black font-bold hover:bg-[#34C759]/80 disabled:opacity-50">
                  {applyingRegion ? 'Applying…' : `✓ Create barn "${regionPreview.barn_name}"`}
                </button>
                <button type="button" onClick={() => setRegionPreview(null)} disabled={applyingRegion}
                  className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {/* Grid Fill panel */}
          {gridFillIdx !== null && markers[gridFillIdx] && (
            <div className="mt-2 p-2.5 bg-[#0D1A0D] border border-[#34C759]/40 rounded">
              <p className="text-[11px] font-bold text-[#34C759] mb-1.5">Grid Fill: {markers[gridFillIdx].barn || `Region ${gridFillIdx + 1}`}</p>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="text-[11px] text-[#9E9E9E]">Rows
                  <input type="number" min="1" max="20" value={gridRows} onChange={(e) => setGridRows(parseInt(e.target.value, 10) || 1)}
                    className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center" />
                </label>
                <label className="text-[11px] text-[#9E9E9E]">× Cols
                  <input type="number" min="1" max="100" value={gridCols} onChange={(e) => setGridCols(parseInt(e.target.value, 10) || 1)}
                    className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center" />
                </label>
                <label className="text-[11px] text-[#9E9E9E]">Start #
                  <input type="number" min="1" value={gridStart} onChange={(e) => setGridStart(parseInt(e.target.value, 10) || 1)}
                    className="w-16 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center" />
                </label>
                <span className="text-[11px] text-[#34C759] font-bold">= {gridRows * gridCols} spots</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <label className="text-[10px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={gridReverseTop} onChange={(e) => setGridReverseTop(e.target.checked)} />
                  <span>Reverse first row</span>
                </label>
                <label className="text-[10px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={gridReverseEvery} onChange={(e) => setGridReverseEvery(e.target.checked)} />
                  <span>Serpentine</span>
                </label>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={handleApplyGridFill} disabled={gridApplying}
                  className="px-2 py-1 bg-[#34C759] rounded text-[10px] text-black font-bold hover:bg-[#34C759]/80 disabled:opacity-50">
                  {gridApplying ? 'Creating…' : `✓ Create ${gridRows * gridCols}-spot grid`}
                </button>
                <button type="button" onClick={() => setGridFillIdx(null)} disabled={gridApplying}
                  className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <button
          type="button"
          onClick={() => { setDrawing(!drawing); setSelected(null); }}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-semibold transition-colors ${drawing ? 'bg-[#FF9500] text-black' : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:text-white hover:border-[#FF9500]/50'}`}
          data-testid={`extra-loc-draw-btn-${location.id}`}
        >
          <MapTrifold size={11} /> {drawing ? 'Drawing...' : 'Draw Region'}
        </button>
        {/* Always visible so producers can persist an emptied state. */}
        <button
          type="button"
          onClick={saveMarkers}
          disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#0096FF] rounded text-[11px] text-white font-semibold hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50"
          data-testid={`extra-loc-save-markers-${location.id}`}
        >
          {saving ? 'Saving...' : (markers.length === 0 ? 'Save (Clear All)' : 'Save Regions')}
        </button>
        {(location.chart || []).some(b => (b.detail_markers || []).length > 0) && (
          <button
            type="button"
            onClick={fitAllBarns}
            disabled={fittingAll}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#34C759]/15 border border-[#34C759]/40 rounded text-[11px] text-[#34C759] font-semibold hover:bg-[#34C759]/25 disabled:opacity-50 transition-colors"
            title="Shrink every barn's markers to fit the printed cells on this map. Use after Detect Cells if markers look too big or overlap multiple squares."
            data-testid={`extra-loc-fit-all-barns-${location.id}`}
          >
            ⊟ {fittingAll ? 'Fitting…' : 'Fit All Barns'}
          </button>
        )}
        {markers.length > 0 && (
          <button
            type="button"
            onClick={() => { if (window.confirm('Clear all regions?')) setMarkers([]); }}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#161616] border border-[#262626] rounded text-[11px] text-[#FF3B30] hover:border-[#FF3B30]/50 transition-colors"
            data-testid={`extra-loc-clear-markers-${location.id}`}
          >
            <Trash size={11} /> Clear All
          </button>
        )}
        <label className="flex items-center gap-1 px-3 py-1.5 bg-[#161616] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 transition-colors cursor-pointer">
          <UploadSimple size={11} /> Replace Map
          <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
        <button
          type="button"
          onClick={handleAutoDetect}
          disabled={!mapUrl || detecting}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#0096FF]/15 border border-[#0096FF]/30 rounded text-[11px] text-[#0096FF] hover:bg-[#0096FF]/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`extra-loc-autodetect-${location.id}`}
          title="AI vision-based section detection — best for labeled hand-drawn maps"
        >
          <MagicWand size={11} /> {detecting ? 'Detecting…' : 'Auto-Detect (AI)'}
        </button>
        <button
          type="button"
          onClick={handleAutoDetectCells}
          disabled={!mapUrl || detecting}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#34C759]/15 border border-[#34C759]/30 rounded text-[11px] text-[#34C759] hover:bg-[#34C759]/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`extra-loc-autodetect-cells-${location.id}`}
          title="OpenCV per-cell detection — best for grid-style maps with many uniform rectangles"
        >
          <GridFour size={11} /> {detecting ? 'Detecting…' : 'Auto-Detect Cells'}
        </button>
        <button
          type="button"
          onClick={handleAutoDetectNumbers}
          disabled={!mapUrl || detecting}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#AF52DE]/15 border border-[#AF52DE]/30 rounded text-[11px] text-[#AF52DE] hover:bg-[#AF52DE]/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`extra-loc-autodetect-numbers-${location.id}`}
          title="AI reads every printed number on the map (no rectangles needed) — best for RV/parking maps that just list numbered spots along a lane"
        >
          <MagicWand size={11} /> {detecting ? 'Detecting…' : 'Auto-Detect Numbers'}
        </button>
        <button
          type="button"
          onClick={handleAutoDetectBarns}
          disabled={!mapUrl || autoLabeling || detecting}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#FF9500] rounded text-[11px] text-white font-semibold hover:bg-[#FF9500]/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`extra-loc-autodetect-barns-${location.id}`}
          title="Find every labeled barn (A, B, C, I, J...) on the map and create a named region for each. Then Grid-Fill each region."
        >
          🏷 {autoLabeling ? 'Finding barns…' : 'Auto-Detect Barns'}
        </button>
      </div>

      {/* Per-barn floor plans — parity with the primary chart's "Place Stalls"
          modal. Each barn in this extra location's chart gets its own card.
          Mount BarnMapEditor with mapType=`loc:{location.id}` so the existing
          per-barn endpoints (upload/get/detect/fit/snap/save) target this
          location's barns inside `extra_locations.{idx}.chart`. */}
      {(location.chart || []).length > 0 && (
        <ExtraLocationBarnList showId={showId} location={location} onChanged={onChanged} />
      )}
    </div>
  );
};

/**
 * Per-barn floor-plan list for an extra location.
 * Each barn in `location.chart` gets a card with:
 *  - "Place Stalls" button → opens BarnMapEditor in a modal
 *  - Inline "⊟ Fit to Squares" / "⊡ Snap to Squares" once markers exist
 * Uses mapType=`loc:{id}` so the unified per-barn backend dispatches to
 * `extra_locations.{idx}.chart.{barn_idx}` automatically.
 */
const ExtraLocationBarnList = ({ showId, location, onChanged }) => {
  const [editingBarnIdx, setEditingBarnIdx] = useState(null);
  const mapType = `loc:${location.id}`;
  const editingBarn = editingBarnIdx !== null ? (location.chart || [])[editingBarnIdx] : null;

  const handleFit = async (barnIdx) => {
    try {
      const { data } = await api.post(
        `/shows/${showId}/barns/${encodeURIComponent(mapType)}/${barnIdx}/fit-markers-to-printed-cells`,
        null,
        { timeout: 60000 },
      );
      toast.success(data.message || 'Fit markers to printed cells');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fit failed');
    }
  };
  const handleSnap = async (barnIdx, markers) => {
    try {
      const { data } = await api.post(
        `/shows/${showId}/barns/${encodeURIComponent(mapType)}/${barnIdx}/snap-markers-to-cells`,
        { markers },
        { timeout: 240000 },
      );
      toast.success(data.message || `Snapped ${data.snapped_to_cell} markers`);
      onChanged && onChanged();
    } catch (e) {
      if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
        toast.error('Snap timed out (OCR can take 2-4 min on dense maps).');
      } else {
        toast.error(e?.response?.data?.detail || 'Snap failed');
      }
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-[#262626]">
      <p className="text-[10px] text-[#9E9E9E] uppercase tracking-wider mb-2">
        Per-Barn Floor Plans · {location.chart.length} barn{location.chart.length === 1 ? '' : 's'}
      </p>
      <div className="space-y-2">
        {location.chart.map((barn, bi) => {
          const hasMarkers = (barn.detail_markers || []).length > 0;
          const hasFloorPlan = !!barn.detail_map_url;
          return (
            <div
              key={bi}
              className="bg-[#0A0A0A] border border-[#262626] rounded p-2 flex items-center justify-between gap-2 flex-wrap"
              data-testid={`extra-loc-barn-card-${location.id}-${bi}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-sm font-semibold text-white truncate">{barn.barn || `Barn ${bi + 1}`}</span>
                <span className="text-[10px] text-[#9E9E9E]">
                  {(barn.rows || []).reduce((s, r) => s + (r.spots || 0), 0)} spots
                  {hasFloorPlan ? ' · 📷 floor plan' : ''}
                  {hasMarkers ? ` · ${barn.detail_markers.length} marker${barn.detail_markers.length === 1 ? '' : 's'}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setEditingBarnIdx(bi)}
                  className="px-2 py-1 bg-[#0096FF]/10 border border-[#0096FF]/40 rounded text-[10px] text-[#0096FF] hover:bg-[#0096FF]/20 flex items-center gap-1"
                  data-testid={`extra-loc-barn-place-stalls-${location.id}-${bi}`}
                >
                  <MapTrifold size={10} /> Place Stalls
                </button>
                {hasMarkers && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleFit(bi)}
                      title="Look INSIDE each marker's bbox for the printed cell and shrink the marker to it."
                      className="px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/40 rounded text-[10px] text-[#34C759] hover:bg-[#34C759]/20"
                      data-testid={`extra-loc-barn-fit-${location.id}-${bi}`}
                    >
                      ⊟ Fit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSnap(bi, barn.detail_markers)}
                      title="Detect actual stall squares + OCR each printed number, then snap markers to match."
                      className="px-2 py-1 bg-[#0096FF]/10 border border-[#0096FF]/40 rounded text-[10px] text-[#0096FF] hover:bg-[#0096FF]/20"
                      data-testid={`extra-loc-barn-snap-${location.id}-${bi}`}
                    >
                      ⊡ Snap
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {editingBarn && (
        <BarnMapEditor
          showId={showId}
          mapType={mapType}
          barnIdx={editingBarnIdx}
          barnName={editingBarn.barn || `Barn ${editingBarnIdx + 1}`}
          totalSpots={(editingBarn.rows || []).reduce((s, r) => s + (r.spots || 0), 0)}
          detailMapUrl={editingBarn.detail_map_url}
          initialMarkers={editingBarn.detail_markers || []}
          onClose={() => setEditingBarnIdx(null)}
          onSaved={() => { onChanged && onChanged(); }}
        />
      )}
    </div>
  );
};
